import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchOpenRouterCatalog,
  OpenRouterCatalogRecord,
} from "../../dataaccess/fetchOpenRouterCatalog";
import {
  fetchProjectOpenRouterGroups,
  ProjectOpenRouterGroupConfig,
} from "../../dataaccess/fetchProjectOpenRouterGroups";
import { updateProjectOpenRouterGroups } from "../../dataaccess/updateProjectOpenRouterGroups";
import { useUser } from "../../contexts/useUser";
import { getVendorColor, getVendorIcon } from "../ServiceGroupCard";

interface OpenRouterGridRow {
  id: string;
  provider: string;
  providerVariantKey: string;
  name: string;
  canonicalSlug: string;
  contextLength: number | null;
  maxCompletionTokens: number | null;
  completionTokenUsdNanoRaw: string | null;
  promptUsdPerToken: string;
  completionUsdPerToken: string;
  routingProviders: string[];
  capabilityBadges: string[];
  isDeprecated: boolean;
  updatedAt: string;
}

interface GroupedModelRow extends OpenRouterGridRow {
  completionUsdPerMillion: number | null;
}

const DEFAULT_PREMIUM_OUTPUT_THRESHOLD_USD_PER_MILLION = 5;
const COMMON_VENDOR_NAMES = [
  "Amazon Bedrock",
  "Anthropic",
  "Azure",
  "DeepSeek",
  "Google",
  "Google AI Studio",
  "Moonshot AI",
  "OpenAI",
  "Openrouter",
  "xAI",
  "Z.AI"
];
const ACTION_SELECT_ALL = "__action_select_all__";
const ACTION_SELECT_NONE = "__action_select_none__";
const ACTION_SELECT_COMMON = "__action_select_common__";

function normalizeVendorForMatch(vendor: string): string {
  return vendor.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function parsePipeDelimitedIds(value?: string | null): string[] {
  return (value || "")
    .split("|")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

function isOpenRouterModelId(id: string): boolean {
  return /^[a-z0-9.-]+\/[a-z0-9._:-]+$/i.test(id.trim());
}

function extractOpenRouterIds(value?: string | null): string[] {
  return parsePipeDelimitedIds(value).filter((id) => isOpenRouterModelId(id));
}

function toDisplayVendorName(value: string): string {
  const normalized = value.trim();
  if (!normalized) {
    return "Unknown";
  }

  if (/^xai$/i.test(normalized)) {
    return "xAI";
  }

  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function getModelVendorId(modelId?: string | null): string {
  const id = String(modelId || "").trim();
  if (!id || !id.includes("/")) {
    return "unknown";
  }

  return id.split("/")[0].trim().toLowerCase() || "unknown";
}

const COMMON_VENDOR_KEYS = new Set(
  COMMON_VENDOR_NAMES.map((vendor) => normalizeVendorForMatch(vendor))
);

function getProviderLogoSrc(provider: string): string | null {
  const p = provider.toLowerCase();

  if (p.includes("openrouter")) {
    return "/ailogos/openrouter_dark.svg";
  }
  if (p.includes("anthropic") || p.includes("claude")) {
    return "/ailogos/anthropic_white.svg";
  }
  if (p.includes("openai")) {
    return "/ailogos/openai_dark.svg";
  }
  if (p.includes("azure") || p.includes("azureai")) {
    return "/ailogos/azure-color.svg";
  }
  if (p.includes("microsoft")) {
    return "/ailogos/microsoft-color.svg";
  }
  if (p.includes("aws") || p.includes("amazon") || p.includes("bedrock")) {
    return "/ailogos/bedrock-color (3).svg";
  }
  if (p.includes("google cloud") || p.includes("googlecloud") || p.includes("gcp")) {
    return "/ailogos/googlecloud-color.svg";
  }
  if (p.includes("vertex")) {
    return "/ailogos/vertexai-color.svg";
  }
  if (p.includes("google") || p.includes("gemini")) {
    return "/ailogos/gemini.svg";
  }
  if (p.includes("cohere")) {
    return "/ailogos/cohere.svg";
  }
  if (p.includes("cerebras")) {
    return "/ailogos/cerebras-color.svg";
  }
  if (p.includes("cloudflare")) {
    return "/ailogos/cloudflare-color.svg";
  }
  if (p.includes("deepseek")) {
    return "/ailogos/deepseek.svg";
  }
  if (
    p.includes("grok") ||
    p.includes("xai") ||
    p.includes("x-ai") ||
    p.includes("x ai") ||
    p.includes("x.ai")
  ) {
    return "/ailogos/xai_dark.svg";
  }
  if (p.includes("groq")) {
    return "/ailogos/groq.svg";
  }
  if (p.includes("huggingface") || p.includes("hugging face")) {
    return "/ailogos/huggingface-color.svg";
  }
  if (p.includes("inflection")) {
    return "/ailogos/inflectionai_dark.svg";
  }
  if (p.includes("meta") || p.includes("llama")) {
    return "/ailogos/meta-color.svg";
  }
  if (p.includes("perplexity")) {
    return "/ailogos/perplexity-color.svg";
  }
  if (p.includes("ai21")) {
    return "/ailogos/ai21-brand-color.svg";
  }
  if (p.includes("moonshot") || p.includes("moonshotai")) {
    return "/ailogos/moonshot.svg";
  }
  if (p.includes("kimi")) {
    return "/ailogos/kimi-icon.svg";
  }
  if (p.includes("minimax")) {
    return "/ailogos/minimax-color.svg";
  }
  if (p.includes("mistral")) {
    return "/ailogos/mistral-ai_logo.svg";
  }
  if (p.includes("nvidia")) {
    return "/ailogos/nvidia-icon-dark.svg";
  }
  if (p.includes("qwen") || p.includes("alibaba")) {
    return "/ailogos/qwen_dark.svg";
  }
  if (p.includes("sambanova")) {
    return "/ailogos/sambanova-color.svg";
  }
  if (p.includes("voyage")) {
    return "/ailogos/voyage-color.svg";
  }
  if (p.includes("zhipu") || p.includes("glm")) {
    return "/ailogos/zhipu-color.svg";
  }
  if (p.includes("baidu") || p.includes("ernie")) {
    return "/ailogos/baidu-color.svg";
  }
  if (p.includes("tencent") || p.includes("hunyuan")) {
    return "/ailogos/tencent-color.svg";
  }

  return null;
}

function ProviderAvatar({ provider }: { provider: string }) {
  const logoSrc = getProviderLogoSrc(provider);

  if (logoSrc) {
    return (
      <Avatar
        sx={{
          width: 24,
          height: 24,
          bgcolor: "rgba(255,255,255,0.06)",
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt={`${provider} logo`}
          sx={{ width: 16, height: 16, objectFit: "contain" }}
        />
      </Avatar>
    );
  }

  return (
    <Avatar
      sx={{
        width: 24,
        height: 24,
        bgcolor: getVendorColor(provider),
        fontSize: "0.7rem",
      }}
    >
      {getVendorIcon(provider)}
    </Avatar>
  );
}

function formatNanoUsdPerToken(value?: string | null): string {
  if (!value) {
    return "-";
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "-";
  }

  const usdPerToken = parsed / 1_000_000_000;
  if (usdPerToken === 0) {
    return "$0";
  }

  if (usdPerToken < 0.000001) {
    return `$${usdPerToken.toExponential(2)}`;
  }

  return `$${usdPerToken.toFixed(9)}`;
}

function toRows(records: OpenRouterCatalogRecord[]): OpenRouterGridRow[] {
  return records.map((record) => {
    const vendorId = getModelVendorId(record.model.id);
    const providerDisplayName = record.model.provider?.displayName;
    const providerId = (record.model.providerId || "").trim().toLowerCase();
    const shouldUseProviderDisplayName =
      !!providerDisplayName && !!providerId && providerId === vendorId;
    const baseProvider = shouldUseProviderDisplayName
      ? providerDisplayName
      : toDisplayVendorName(vendorId);

    return {
      id: record.model.id,
      provider: baseProvider,
      providerVariantKey: `${record.model.id}@@${baseProvider}`,
    name: record.model.name,
    canonicalSlug: record.model.canonicalSlug || "",
    contextLength: record.model.contextLength ?? null,
    maxCompletionTokens: record.model.maxCompletionTokens ?? null,
    completionTokenUsdNanoRaw: record.currentPricing?.completionTokenUsdNano || null,
    promptUsdPerToken: formatNanoUsdPerToken(record.currentPricing?.promptTokenUsdNano),
    completionUsdPerToken: formatNanoUsdPerToken(record.currentPricing?.completionTokenUsdNano),
    routingProviders: record.routingProviders || [],
    capabilityBadges: record.capabilityBadges || [],
    isDeprecated: !!record.model.isDeprecated,
    updatedAt: record.model.updatedAt || "",
    };
  });
}

function getModelDisplayName(name: string): string {
  const separatorIndex = name.indexOf(":");
  if (separatorIndex === -1) {
    return name;
  }

  return name.slice(separatorIndex + 1).trim() || name;
}

function formatModelSubtitle(prefix: "Latest" | "Older", row: GroupedModelRow): string {
  const contextLabel =
    row.contextLength && row.contextLength > 0
      ? `${row.contextLength.toLocaleString()} ctx`
      : "- ctx";
  const providerCount = Math.max(row.routingProviders.length, 1);
  const providerLabel = `${providerCount} provider${providerCount === 1 ? "" : "s"}`;

  return `${prefix} • ${row.completionUsdPerMillion?.toFixed(2) ?? "-"}/M output • ${contextLabel} • ${providerLabel}`;
}

function getVisibleCapabilityBadges(row: GroupedModelRow): string[] {
  return row.capabilityBadges.slice(0, 4);
}

function renderModelSubtitle(prefix: "Latest" | "Older", row: GroupedModelRow) {
  const badges = getVisibleCapabilityBadges(row);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1.25 }}>
        {formatModelSubtitle(prefix, row)}
      </Typography>
      {badges.map((badge) => (
        <Chip
          key={`${row.providerVariantKey}-${badge}`}
          label={badge}
          size="small"
          variant="outlined"
          sx={{ height: 18, "& .MuiChip-label": { px: 0.75, fontSize: "0.65rem" } }}
        />
      ))}
    </Box>
  );
}

function parseCompletionUsdPerMillion(value?: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed / 1000;
}

function groupIntoStandardAndPremium(
  rows: OpenRouterGridRow[],
  premiumOutputThresholdUsdPerMillion: number
) {
  const activeRows: GroupedModelRow[] = rows
    .filter((row) => !row.isDeprecated)
    .map((row) => ({
      ...row,
      completionUsdPerMillion: parseCompletionUsdPerMillion(row.completionTokenUsdNanoRaw),
    }));

  const standard: GroupedModelRow[] = [];
  const premium: GroupedModelRow[] = [];

  for (const row of activeRows) {
    const completionPerMillion = row.completionUsdPerMillion;

    if (
      completionPerMillion !== null &&
      completionPerMillion > premiumOutputThresholdUsdPerMillion
    ) {
      premium.push(row);
    } else {
      standard.push(row);
    }
  }

  const byPriceThenName = (a: GroupedModelRow, b: GroupedModelRow) => {
    const priceDiff =
      (a.completionUsdPerMillion ?? Number.POSITIVE_INFINITY) -
      (b.completionUsdPerMillion ?? Number.POSITIVE_INFINITY);
    if (priceDiff !== 0) {
      return priceDiff;
    }

    if (a.provider !== b.provider) {
      return a.provider.localeCompare(b.provider);
    }

    return a.name.localeCompare(b.name);
  };

  return {
    standard: [...standard].sort(byPriceThenName),
    premium: [...premium].sort(byPriceThenName),
    totalActive: activeRows.length,
  };
}

function groupByVendor(rows: GroupedModelRow[]) {
  const grouped = rows.reduce<Record<string, GroupedModelRow[]>>((acc, row) => {
    if (!acc[row.provider]) {
      acc[row.provider] = [];
    }
    acc[row.provider].push(row);
    return acc;
  }, {});

  return Object.keys(grouped)
    .sort((a, b) => a.localeCompare(b))
    .map((vendor) => ({
      vendor,
      rows: (grouped[vendor] || []).sort((a, b) => {
        const priceDiff =
          (a.completionUsdPerMillion ?? Number.POSITIVE_INFINITY) -
          (b.completionUsdPerMillion ?? Number.POSITIVE_INFINITY);
        if (priceDiff !== 0) {
          return priceDiff;
        }

        return a.name.localeCompare(b.name);
      }),
    }))
    .filter((group) => group.rows.length > 0);
}

function normalizeModelName(name: string): string {
  let normalized = name.toLowerCase().trim();

  const colonIndex = normalized.indexOf(":");
  if (colonIndex !== -1) {
    normalized = normalized.slice(colonIndex + 1).trim();
  }

  normalized = normalized.replace(/\([^)]*\)/g, " ");
  normalized = normalized.replace(/[,_/\-]+/g, " ");
  normalized = normalized.replace(/\s+/g, " ").trim();

  return normalized;
}

function isVersionToken(token: string): boolean {
  if (!token) {
    return false;
  }

  const cleaned = token.toLowerCase().replace(/^v/, "");

  if (/^\d+(\.\d+){0,3}$/.test(cleaned)) {
    return true;
  }

  if (/^\d+(\.\d+){0,3}[a-z]$/.test(cleaned)) {
    return true;
  }

  if (/^[a-z]+\d+(\.\d+){0,3}$/.test(cleaned)) {
    return true;
  }

  if (/^\d{4}[-.]\d{1,2}([-.]\d{1,2})?$/.test(cleaned)) {
    return true;
  }

  return false;
}

function parseVersionParts(token: string): number[] {
  const cleaned = token.toLowerCase().replace(/^v/, "").replace(/-/g, ".");

  const leadingAlphaMatch = cleaned.match(/^([a-z]+)(\d+(?:\.\d+)*)$/);
  if (leadingAlphaMatch) {
    return leadingAlphaMatch[2]
      .split(".")
      .map((part) => Number(part))
      .filter((part) => Number.isFinite(part));
  }

  const trailingAlphaMatch = cleaned.match(/^(\d+(?:\.\d+){0,3})([a-z])$/);
  if (trailingAlphaMatch) {
    const numericParts = trailingAlphaMatch[1]
      .split(".")
      .map((part) => Number(part))
      .filter((part) => Number.isFinite(part));
    const suffixScore = trailingAlphaMatch[2].charCodeAt(0) - 96;
    return [...numericParts, suffixScore];
  }

  return cleaned
    .split(".")
    .map((part) => Number(part))
    .filter((part) => Number.isFinite(part));
}

function compareVersionParts(a: number[], b: number[]): number {
  const length = Math.max(a.length, b.length);

  for (let i = 0; i < length; i += 1) {
    const left = a[i] ?? 0;
    const right = b[i] ?? 0;

    if (left !== right) {
      return left - right;
    }
  }

  return 0;
}

function extractFamilyAndVersion(name: string): {
  familyKey: string;
  versionParts: number[] | null;
} {
  const normalized = normalizeModelName(name);
  const tokens = normalized.split(" ").filter(Boolean);

  if (tokens.length === 0) {
    return { familyKey: normalized, versionParts: null };
  }

  const isClaudeModel = tokens.includes("claude");
  if (isClaudeModel) {
    const claudeTier = ["haiku", "sonnet", "opus"].find((tier) =>
      tokens.includes(tier)
    );

    if (claudeTier) {
      const versionToken = tokens.find((token) => isVersionToken(token));
      const versionParts = versionToken ? parseVersionParts(versionToken) : null;

      return {
        familyKey: `claude ${claudeTier}`,
        versionParts: versionParts && versionParts.length > 0 ? versionParts : null,
      };
    }
  }

  const versionIndex = tokens.findIndex((token) => isVersionToken(token));

  if (versionIndex === -1) {
    return {
      familyKey: tokens.join(" "),
      versionParts: null,
    };
  }

  const familyTokens = tokens.slice(0, versionIndex);
  const versionToken = tokens[versionIndex];

  if (familyTokens.length === 0) {
    const alphaPrefix = versionToken.match(/^([a-z]+)\d/);
    if (alphaPrefix) {
      familyTokens.push(alphaPrefix[1]);
    }
  }
  const baseFamilyTokens = familyTokens.length > 0 ? familyTokens : [tokens[0]];
  const familyKey = baseFamilyTokens.join(" ");
  const versionParts = parseVersionParts(tokens[versionIndex]);

  return {
    familyKey,
    versionParts: versionParts.length > 0 ? versionParts : null,
  };
}

function compareLatestCandidate(a: GroupedModelRow, b: GroupedModelRow): number {
  const parsedA = extractFamilyAndVersion(a.name);
  const parsedB = extractFamilyAndVersion(b.name);

  const aHasVersion = parsedA.versionParts ? 1 : 0;
  const bHasVersion = parsedB.versionParts ? 1 : 0;

  if (aHasVersion !== bHasVersion) {
    return aHasVersion - bHasVersion;
  }

  if (parsedA.versionParts && parsedB.versionParts) {
    const versionCompare = compareVersionParts(
      parsedA.versionParts,
      parsedB.versionParts
    );
    if (versionCompare !== 0) {
      return versionCompare;
    }
  }

  const updatedA = a.updatedAt ? Date.parse(a.updatedAt) : 0;
  const updatedB = b.updatedAt ? Date.parse(b.updatedAt) : 0;
  if (updatedA !== updatedB) {
    return updatedA - updatedB;
  }

  return a.name.localeCompare(b.name);
}

function splitLatestAndOlderVendorRows(rows: GroupedModelRow[]) {
  if (rows.length === 0) {
    return { latest: [] as GroupedModelRow[], older: [] as GroupedModelRow[] };
  }

  const families = rows.reduce<Record<string, GroupedModelRow[]>>((acc, row) => {
    const { familyKey } = extractFamilyAndVersion(row.name);
    if (!acc[familyKey]) {
      acc[familyKey] = [];
    }
    acc[familyKey].push(row);
    return acc;
  }, {});

  const latest: GroupedModelRow[] = [];
  const older: GroupedModelRow[] = [];

  for (const familyRows of Object.values(families)) {
    const parsedRows = familyRows.map((row) => ({
      row,
      parsed: extractFamilyAndVersion(row.name),
    }));

    const withVersion = parsedRows.filter((item) => item.parsed.versionParts);

    if (withVersion.length > 0) {
      let maxVersion = withVersion[0].parsed.versionParts as number[];

      for (const item of withVersion.slice(1)) {
        const compare = compareVersionParts(
          item.parsed.versionParts as number[],
          maxVersion
        );
        if (compare > 0) {
          maxVersion = item.parsed.versionParts as number[];
        }
      }

      for (const item of parsedRows) {
        const versionParts = item.parsed.versionParts;
        if (versionParts && compareVersionParts(versionParts, maxVersion) === 0) {
          latest.push(item.row);
        } else {
          older.push(item.row);
        }
      }
      continue;
    }

    let best = familyRows[0];
    for (const row of familyRows.slice(1)) {
      if (compareLatestCandidate(row, best) > 0) {
        best = row;
      }
    }

    latest.push(best);
    older.push(...familyRows.filter((row) => row.id !== best.id));
  }

  const byPriceThenName = (a: GroupedModelRow, b: GroupedModelRow) => {
    const priceDiff =
      (a.completionUsdPerMillion ?? Number.POSITIVE_INFINITY) -
      (b.completionUsdPerMillion ?? Number.POSITIVE_INFINITY);
    if (priceDiff !== 0) {
      return priceDiff;
    }

    return a.name.localeCompare(b.name);
  };

  return {
    latest: latest.sort(byPriceThenName),
    older: older.sort(byPriceThenName),
  };
}

function pickLatestRow(rows: GroupedModelRow[]): GroupedModelRow | null {
  if (rows.length === 0) {
    return null;
  }

  let best = rows[0];
  for (const row of rows.slice(1)) {
    if (compareLatestCandidate(row, best) > 0) {
      best = row;
    }
  }

  return best;
}

function findPreferredModelKeys(rows: GroupedModelRow[]): Set<string> {
  const preferredKeys = new Set<string>();

  const addPreferredById = (modelId: string) => {
    const match = rows.find((row) => row.id.toLowerCase() === modelId.toLowerCase());
    if (match) {
      preferredKeys.add(match.providerVariantKey);
    }
  };

  const addPreferredByIdContains = (idFragment: string) => {
    const match = rows.find((row) => row.id.toLowerCase().includes(idFragment.toLowerCase()));
    if (match) {
      preferredKeys.add(match.providerVariantKey);
    }
  };

  const anthropicRows = rows.filter((row) =>
    row.provider.toLowerCase().includes("anthropic")
  );

  const sonnetLatest = pickLatestRow(
    anthropicRows.filter((row) => normalizeModelName(row.name).includes("sonnet"))
  );
  if (sonnetLatest) {
    preferredKeys.add(sonnetLatest.providerVariantKey);
  }

  const haikuLatest = pickLatestRow(
    anthropicRows.filter((row) => normalizeModelName(row.name).includes("haiku"))
  );
  if (haikuLatest) {
    preferredKeys.add(haikuLatest.providerVariantKey);
  }

  const openAiNonCodexGptRows = rows.filter((row) => {
    const normalized = normalizeModelName(row.name);
    const modelId = row.id.toLowerCase();
    return (
      modelId.startsWith("openai/") &&
      normalized.includes("gpt") &&
      !normalized.includes("codex")
    );
  });

  const openAiBaseGptRows = openAiNonCodexGptRows.filter((row) => {
    const modelId = row.id.toLowerCase();
    return /^openai\/gpt-\d+(\.\d+)*$/.test(modelId);
  });

  const openAiLatestGpt = pickLatestRow(
    openAiBaseGptRows.length > 0 ? openAiBaseGptRows : openAiNonCodexGptRows
  );
  if (openAiLatestGpt) {
    preferredKeys.add(openAiLatestGpt.providerVariantKey);
  }

  addPreferredById("openrouter/auto");
  addPreferredById("openai/gpt-5-mini");
  addPreferredByIdContains("openai/gpt-5-mini");

  return preferredKeys;
}

export default function AdminOpenRouterModels() {
  const navigate = useNavigate();
  const { project } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [records, setRecords] = useState<OpenRouterCatalogRecord[]>([]);
  const [projectGroupConfig, setProjectGroupConfig] =
    useState<ProjectOpenRouterGroupConfig | null>(null);
  const [hasLoadedCatalogOnce, setHasLoadedCatalogOnce] = useState(false);
  const [hasLoadedProjectGroupsOnce, setHasLoadedProjectGroupsOnce] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [premiumCutoffInput, setPremiumCutoffInput] = useState<string>(
    String(DEFAULT_PREMIUM_OUTPUT_THRESHOLD_USD_PER_MILLION)
  );
  const [hideOlderModels, setHideOlderModels] = useState(false);
  const [showOtherVendors, setShowOtherVendors] = useState(false);
  const [selectedStandardModelKeys, setSelectedStandardModelKeys] = useState<string[]>([]);
  const [selectedPremiumModelKeys, setSelectedPremiumModelKeys] = useState<string[]>([]);
  const hasAttemptedInitialAutoSaveRef = useRef(false);

  const rows = useMemo(() => toRows(records), [records]);
  const vendorOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((row) => row.provider))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [rows]
  );

  const getCommonVendors = (vendors: string[]) => {
    return vendors.filter((vendor) =>
      COMMON_VENDOR_KEYS.has(normalizeVendorForMatch(vendor))
    );
  };

  useEffect(() => {
    if (rows.length === 0) {
      return;
    }

    setSelectedVendors((prev) => {
      if (prev.length > 0) {
        return prev;
      }

      const allVendors = Array.from(new Set(rows.map((row) => row.provider))).sort(
        (a, b) => a.localeCompare(b)
      );
      const commonVendors = getCommonVendors(allVendors);

      return commonVendors.length > 0 ? commonVendors : allVendors;
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (selectedVendors.length === 0) {
      return rows;
    }

    const selected = new Set(selectedVendors);
    return rows.filter((row) => selected.has(row.provider));
  }, [rows, selectedVendors]);

  const commonVendorOptions = useMemo(
    () => getCommonVendors(vendorOptions),
    [vendorOptions]
  );

  const otherVendorOptions = useMemo(
    () => vendorOptions.filter((vendor) => !commonVendorOptions.includes(vendor)),
    [vendorOptions, commonVendorOptions]
  );

  const premiumCutoffUsdPerMillion = useMemo(() => {
    const parsed = Number(premiumCutoffInput);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return DEFAULT_PREMIUM_OUTPUT_THRESHOLD_USD_PER_MILLION;
    }
    return parsed;
  }, [premiumCutoffInput]);

  const grouped = useMemo(
    () => groupIntoStandardAndPremium(filteredRows, premiumCutoffUsdPerMillion),
    [filteredRows, premiumCutoffUsdPerMillion]
  );
  const standardByVendor = useMemo(() => groupByVendor(grouped.standard), [grouped.standard]);
  const premiumByVendor = useMemo(() => groupByVendor(grouped.premium), [grouped.premium]);

  const standardModelKeys = useMemo(
    () => grouped.standard.map((row) => row.providerVariantKey),
    [grouped.standard]
  );
  const premiumModelKeys = useMemo(
    () => grouped.premium.map((row) => row.providerVariantKey),
    [grouped.premium]
  );
  const allVisibleRows = useMemo(
    () => [...grouped.standard, ...grouped.premium],
    [grouped.standard, grouped.premium]
  );

  const preferredModelKeys = useMemo(
    () => findPreferredModelKeys(allVisibleRows),
    [allVisibleRows]
  );

  const storedStandardOpenRouterIds = useMemo(() => {
    const sourceValue =
      typeof projectGroupConfig?.openrouterStandardGroupModels === "string"
        ? projectGroupConfig.openrouterStandardGroupModels
        : (projectGroupConfig?.standardGroupModels ?? project?.standardGroupModels ?? "");

    return extractOpenRouterIds(sourceValue);
  }, [
    projectGroupConfig?.openrouterStandardGroupModels,
    projectGroupConfig?.standardGroupModels,
    project?.standardGroupModels,
  ]);

  const storedPremiumOpenRouterIds = useMemo(() => {
    const sourceValue =
      typeof projectGroupConfig?.openrouterPremiumGroupModels === "string"
        ? projectGroupConfig.openrouterPremiumGroupModels
        : (projectGroupConfig?.premiumGroupModels ?? project?.premiumGroupModels ?? "");

    return extractOpenRouterIds(sourceValue);
  }, [
    projectGroupConfig?.openrouterPremiumGroupModels,
    projectGroupConfig?.premiumGroupModels,
    project?.premiumGroupModels,
  ]);

  const hasStoredOpenRouterSelections =
    storedStandardOpenRouterIds.length > 0 || storedPremiumOpenRouterIds.length > 0;

  useEffect(() => {
    setSelectedStandardModelKeys((prev) => {
      const available = new Set(standardModelKeys);
      const kept = prev.filter((key) => available.has(key));

      if (prev.length > 0) {
        return kept;
      }

      if (hasStoredOpenRouterSelections) {
        const storedKeys = grouped.standard
          .filter((row) => storedStandardOpenRouterIds.includes(row.id))
          .map((row) => row.providerVariantKey);

        return storedKeys.filter((key) => available.has(key));
      }

      const suggestedStandardKeys = grouped.standard
        .filter((row) => preferredModelKeys.has(row.providerVariantKey))
        .map((row) => row.providerVariantKey)
        .filter((key) => available.has(key));

      if (suggestedStandardKeys.length > 0) {
        return suggestedStandardKeys;
      }

      return suggestedStandardKeys;
    });
  }, [
    standardModelKeys,
    grouped.standard,
    hasStoredOpenRouterSelections,
    storedStandardOpenRouterIds,
    preferredModelKeys,
  ]);

  useEffect(() => {
    setSelectedPremiumModelKeys((prev) => {
      const available = new Set(premiumModelKeys);
      const kept = prev.filter((key) => available.has(key));

      if (prev.length > 0) {
        return kept;
      }

      if (hasStoredOpenRouterSelections) {
        const storedKeys = grouped.premium
          .filter((row) => storedPremiumOpenRouterIds.includes(row.id))
          .map((row) => row.providerVariantKey);

        return storedKeys.filter((key) => available.has(key));
      }

      const suggestedPremiumKeys = grouped.premium
        .filter((row) => preferredModelKeys.has(row.providerVariantKey))
        .map((row) => row.providerVariantKey)
        .filter((key) => available.has(key));

      return suggestedPremiumKeys;

    });
  }, [
    premiumModelKeys,
    grouped.premium,
    hasStoredOpenRouterSelections,
    storedPremiumOpenRouterIds,
    preferredModelKeys,
  ]);

  const selectedStandardKeySet = useMemo(
    () => new Set(selectedStandardModelKeys),
    [selectedStandardModelKeys]
  );
  const selectedPremiumKeySet = useMemo(
    () => new Set(selectedPremiumModelKeys),
    [selectedPremiumModelKeys]
  );

  const standardSelectedTextList = useMemo(
    () => {
      const selectedIds = grouped.standard
        .filter((row) => selectedStandardKeySet.has(row.providerVariantKey))
        .map((row) => row.id);

      return Array.from(new Set(selectedIds)).join("|");
    },
    [grouped.standard, selectedStandardKeySet]
  );

  const premiumSelectedTextList = useMemo(
    () => {
      const selectedIds = grouped.premium
        .filter((row) => selectedPremiumKeySet.has(row.providerVariantKey))
        .map((row) => row.id);

      return Array.from(new Set(selectedIds)).join("|");
    },
    [grouped.premium, selectedPremiumKeySet]
  );

  const handleSelectAllVendors = () => {
    setSelectedVendors(vendorOptions);
  };

  const handleSelectNoVendors = () => {
    setSelectedVendors([]);
  };

  const handleSelectCommonVendors = () => {
    const common = getCommonVendors(vendorOptions);

    setSelectedVendors(common);
  };

  const toggleVendor = (vendor: string) => {
    setSelectedVendors((prev) =>
      prev.includes(vendor) ? prev.filter((item) => item !== vendor) : [...prev, vendor]
    );
  };

  const toggleStandardModel = (key: string) => {
    setSelectedStandardModelKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const togglePremiumModel = (key: string) => {
    setSelectedPremiumModelKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const handleSelectNoModels = () => {
    setSelectedStandardModelKeys([]);
    setSelectedPremiumModelKeys([]);
  };

  const handleSelectPreferredModels = () => {
    setSelectedStandardModelKeys(
      grouped.standard
        .filter((row) => preferredModelKeys.has(row.providerVariantKey))
        .map((row) => row.providerVariantKey)
    );
    setSelectedPremiumModelKeys(
      grouped.premium
        .filter((row) => preferredModelKeys.has(row.providerVariantKey))
        .map((row) => row.providerVariantKey)
    );
  };

  const loadCatalog = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchOpenRouterCatalog();
      setRecords(data);
    } catch (err) {
      setError(`Failed to load OpenRouter catalog: ${String(err)}`);
    } finally {
      setLoading(false);
      setHasLoadedCatalogOnce(true);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    if (!project?.id) {
      setProjectGroupConfig(null);
      setHasLoadedProjectGroupsOnce(true);
      return;
    }

    let cancelled = false;
    setHasLoadedProjectGroupsOnce(false);

    const loadProjectGroupConfig = async () => {
      try {
        const data = await fetchProjectOpenRouterGroups(project.id);
        if (!cancelled) {
          setProjectGroupConfig(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(`Failed to load project OpenRouter selections: ${String(err)}`);
        }
      } finally {
        if (!cancelled) {
          setHasLoadedProjectGroupsOnce(true);
        }
      }
    };

    loadProjectGroupConfig();

    return () => {
      cancelled = true;
    };
  }, [project?.id]);

  const selectedStandardIds = useMemo(
    () =>
      grouped.standard
        .filter((row) => selectedStandardKeySet.has(row.providerVariantKey))
        .map((row) => row.id),
    [grouped.standard, selectedStandardKeySet]
  );

  const selectedPremiumIds = useMemo(
    () =>
      grouped.premium
        .filter((row) => selectedPremiumKeySet.has(row.providerVariantKey))
        .map((row) => row.id),
    [grouped.premium, selectedPremiumKeySet]
  );

  const normalizedStoredStandardIds = useMemo(
    () => Array.from(new Set(storedStandardOpenRouterIds)).sort(),
    [storedStandardOpenRouterIds]
  );
  const normalizedStoredPremiumIds = useMemo(
    () => Array.from(new Set(storedPremiumOpenRouterIds)).sort(),
    [storedPremiumOpenRouterIds]
  );

  const normalizedSelectedStandardIds = useMemo(
    () => Array.from(new Set(selectedStandardIds)).sort(),
    [selectedStandardIds]
  );
  const normalizedSelectedPremiumIds = useMemo(
    () => Array.from(new Set(selectedPremiumIds)).sort(),
    [selectedPremiumIds]
  );

  const hasUnsavedChanges = useMemo(() => {
    const sameLengthStandard =
      normalizedStoredStandardIds.length === normalizedSelectedStandardIds.length;
    const sameLengthPremium =
      normalizedStoredPremiumIds.length === normalizedSelectedPremiumIds.length;

    if (!sameLengthStandard || !sameLengthPremium) {
      return true;
    }

    const sameStandard = normalizedStoredStandardIds.every(
      (id, index) => id === normalizedSelectedStandardIds[index]
    );
    const samePremium = normalizedStoredPremiumIds.every(
      (id, index) => id === normalizedSelectedPremiumIds[index]
    );

    return !(sameStandard && samePremium);
  }, [
    normalizedStoredStandardIds,
    normalizedSelectedStandardIds,
    normalizedStoredPremiumIds,
    normalizedSelectedPremiumIds,
  ]);

  const shouldShowUnsavedChanges =
    hasUnsavedChanges && hasLoadedCatalogOnce && hasLoadedProjectGroupsOnce;

  useEffect(() => {
    if (hasStoredOpenRouterSelections) {
      return;
    }

    if (hasAttemptedInitialAutoSaveRef.current) {
      return;
    }

    if (!hasLoadedCatalogOnce || !hasLoadedProjectGroupsOnce) {
      return;
    }

    if (!project?.id) {
      return;
    }

    if (!hasUnsavedChanges) {
      return;
    }

    if (
      normalizedSelectedStandardIds.length === 0 &&
      normalizedSelectedPremiumIds.length === 0
    ) {
      return;
    }

    hasAttemptedInitialAutoSaveRef.current = true;

    const saveInitialAutoSelections = async () => {
      setSaving(true);
      setSaveError(null);

      try {
        const nextOpenRouterStandardGroupModels = normalizedSelectedStandardIds.join("|");
        const nextOpenRouterPremiumGroupModels = normalizedSelectedPremiumIds.join("|");

        await updateProjectOpenRouterGroups({
          id: project.id,
          openrouterStandardGroupModels: nextOpenRouterStandardGroupModels,
          openrouterPremiumGroupModels: nextOpenRouterPremiumGroupModels,
        });

        setProjectGroupConfig((prev) => ({
          id: project.id,
          standardGroupModels:
            prev?.standardGroupModels ?? project.standardGroupModels ?? null,
          premiumGroupModels:
            prev?.premiumGroupModels ?? project.premiumGroupModels ?? null,
          openrouterStandardGroupModels: nextOpenRouterStandardGroupModels,
          openrouterPremiumGroupModels: nextOpenRouterPremiumGroupModels,
        }));

        setSaveMessage("Saved OpenRouter selections.");
      } catch (err) {
        setSaveError(
          `Failed to save initial OpenRouter selections automatically: ${String(err)}`
        );
      } finally {
        setSaving(false);
      }
    };

    void saveInitialAutoSelections();
  }, [
    hasStoredOpenRouterSelections,
    hasLoadedCatalogOnce,
    hasLoadedProjectGroupsOnce,
    hasUnsavedChanges,
    project?.id,
    project?.standardGroupModels,
    project?.premiumGroupModels,
    normalizedSelectedStandardIds,
    normalizedSelectedPremiumIds,
  ]);

  const selectedStandardModelChips = useMemo(
    () =>
      grouped.standard
        .filter((row) => selectedStandardKeySet.has(row.providerVariantKey))
        .map((row) => ({
          key: row.providerVariantKey,
          label: getModelDisplayName(row.name),
          id: row.id,
        })),
    [grouped.standard, selectedStandardKeySet]
  );

  const selectedPremiumModelChips = useMemo(
    () =>
      grouped.premium
        .filter((row) => selectedPremiumKeySet.has(row.providerVariantKey))
        .map((row) => ({
          key: row.providerVariantKey,
          label: getModelDisplayName(row.name),
          id: row.id,
        })),
    [grouped.premium, selectedPremiumKeySet]
  );

  const handleSaveSelections = async () => {
    if (!project?.id) {
      setSaveError("Project is not loaded yet.");
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveMessage(null);

    try {
      const nextOpenRouterStandardGroupModels = Array.from(
        new Set(selectedStandardIds)
      ).join("|");
      const nextOpenRouterPremiumGroupModels = Array.from(
        new Set(selectedPremiumIds)
      ).join("|");

      await updateProjectOpenRouterGroups({
        id: project.id,
        openrouterStandardGroupModels: nextOpenRouterStandardGroupModels,
        openrouterPremiumGroupModels: nextOpenRouterPremiumGroupModels,
      });

      setSaveMessage("Saved OpenRouter selections.");

      setProjectGroupConfig((prev) => ({
        id: project.id,
        standardGroupModels: prev?.standardGroupModels ?? project.standardGroupModels ?? null,
        premiumGroupModels: prev?.premiumGroupModels ?? project.premiumGroupModels ?? null,
        openrouterStandardGroupModels: nextOpenRouterStandardGroupModels,
        openrouterPremiumGroupModels: nextOpenRouterPremiumGroupModels,
      }));
    } catch (err) {
      setSaveError(`Failed to save OpenRouter selections: ${String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const openChatTestForModel = (modelId: string) => {
    navigate(`/chat?service=${encodeURIComponent(`model:${modelId}`)}`);
  };

  const openChatTestForServiceGroup = (serviceGroupId: string) => {
    navigate(`/chat?service=${encodeURIComponent(serviceGroupId)}`);
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setSaveMessage(`${label} copied.`);
      setSaveError(null);
    } catch (err) {
      setSaveError(`Failed to copy ${label}: ${String(err)}`);
    }
  };

  const renderModelActions = (
    row: GroupedModelRow,
    selected: boolean,
    onToggle: () => void
  ) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Tooltip title={`Test ${row.id}`}>
        <IconButton
          size="small"
          aria-label={`Test ${row.id}`}
          onClick={() => openChatTestForModel(row.id)}
          sx={{ p: 0.35 }}
        >
          <PlayArrowIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={`Copy ${row.id}`}>
        <IconButton
          size="small"
          aria-label={`Copy ${row.id}`}
          onClick={() => copyToClipboard(row.id, `Model ID (${row.id})`)}
          sx={{ p: 0.35 }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Switch edge="end" size="small" checked={selected} onChange={onToggle} />
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", lg: "nowrap" },
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <FormControl size="small" sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }}>
          <InputLabel id="openrouter-vendor-filter-label">Model Vendor Filter</InputLabel>
          <Select
            labelId="openrouter-vendor-filter-label"
            multiple
            value={selectedVendors}
            label="Model Vendor Filter"
            MenuProps={{
              MenuListProps: {
                dense: true,
                sx: { py: 0.5 },
              },
              PaperProps: {
                sx: { maxHeight: 420, width: 340 },
              },
            }}
            sx={{
              "& .MuiSelect-select": {
                py: 1,
              },
            }}
            onChange={() => {
              // Intentionally no-op: selection is handled explicitly in MenuItem click handlers
            }}
            renderValue={(selected) => {
              const selectedList = selected as string[];
              if (selectedList.length === 0) {
                return "No model vendors selected";
              }
              if (selectedList.length === vendorOptions.length) {
                return `All model vendors (${vendorOptions.length})`;
              }
              if (selectedList.length > 2) {
                return `${selectedList.length} model vendors selected`;
              }
              return selectedList.join(", ");
            }}
          >
            <MenuItem
              value={ACTION_SELECT_ALL}
              sx={{ fontWeight: 600, minHeight: 32, py: 0.5 }}
              onClick={handleSelectAllVendors}
            >
              Select all
            </MenuItem>
            <MenuItem
              value={ACTION_SELECT_NONE}
              sx={{ fontWeight: 600, minHeight: 32, py: 0.5 }}
              onClick={handleSelectNoVendors}
            >
              Select none
            </MenuItem>
            <MenuItem
              value={ACTION_SELECT_COMMON}
              sx={{ fontWeight: 600, minHeight: 32, py: 0.5 }}
              onClick={handleSelectCommonVendors}
            >
              Select common
            </MenuItem>
            <Divider />

            {commonVendorOptions.length > 0 && (
              <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, minHeight: 30, py: 0.25 }}>
                Common model vendors
              </MenuItem>
            )}
            {commonVendorOptions.map((vendor) => (
              <MenuItem
                key={vendor}
                value={vendor}
                sx={{ minHeight: 32, py: 0.25 }}
                onClick={() => toggleVendor(vendor)}
              >
                <Checkbox checked={selectedVendors.includes(vendor)} size="small" />
                <ListItemText primary={vendor} primaryTypographyProps={{ fontSize: "0.95rem" }} />
              </MenuItem>
            ))}

            {otherVendorOptions.length > 0 && (
              <>
                <Divider />
                <Box
                  sx={{ px: 2, py: 0.75, fontWeight: 600, cursor: "pointer", userSelect: "none" }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setShowOtherVendors((prev) => !prev);
                  }}
                >
                  {showOtherVendors
                    ? "Hide other model vendors"
                    : `Other model vendors (${otherVendorOptions.length})`}
                </Box>
                {showOtherVendors &&
                  otherVendorOptions.map((vendor) => (
                    <MenuItem
                      key={vendor}
                      value={vendor}
                      sx={{ pl: 4, minHeight: 32, py: 0.25 }}
                      onClick={() => toggleVendor(vendor)}
                    >
                      <Checkbox checked={selectedVendors.includes(vendor)} size="small" />
                      <ListItemText primary={vendor} primaryTypographyProps={{ fontSize: "0.95rem" }} />
                    </MenuItem>
                  ))}
              </>
            )}
          </Select>
        </FormControl>

        <TextField
          size="small"
          type="number"
          label="Premium cutoff ($/M output)"
          value={premiumCutoffInput}
          onChange={(event) => setPremiumCutoffInput(event.target.value)}
          inputProps={{ min: 0, step: 0.1 }}
          sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}
          helperText="Models above this value are Premium"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={hideOlderModels}
              onChange={(event) => setHideOlderModels(event.target.checked)}
              size="small"
            />
          }
          label="Hide older models"
          sx={{ m: 0, mt: 0.75, ml: { xs: 0, md: 1 }, whiteSpace: "nowrap", flexShrink: 0 }}
        />

        <Box sx={{ ml: { xs: 0, lg: "auto" }, mt: 0.5, flexShrink: 0 }}>
          <Button variant="outlined" onClick={loadCatalog} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button size="small" variant="outlined" onClick={handleSelectNoModels}>
          Select None
        </Button>
        <Button size="small" variant="outlined" onClick={handleSelectPreferredModels}>
          Select Preferred
        </Button>
        <Button
          size="small"
          variant="contained"
          color={shouldShowUnsavedChanges ? "warning" : "primary"}
          onClick={handleSaveSelections}
          disabled={saving || !project?.id}
          sx={shouldShowUnsavedChanges ? { fontWeight: 700, boxShadow: 3 } : undefined}
        >
          {saving ? "Saving..." : shouldShowUnsavedChanges ? "Save Changes" : "Save Selections"}
        </Button>
      </Box>

      {shouldShowUnsavedChanges && (
        <Alert severity="warning">Unsaved changes</Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading OpenRouter models...
          </Typography>
        </Box>
      )}

      {saveMessage && <Alert severity="success">{saveMessage}</Alert>}
      {saveError && <Alert severity="error">{saveError}</Alert>}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6">Standard</Typography>
                <Chip label={`Selected ${selectedStandardIds.length}`} size="small" />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title="Test openrouter:standard">
                  <IconButton
                    size="small"
                    aria-label="Test openrouter standard group"
                    onClick={() => openChatTestForServiceGroup("openrouter:standard")}
                  >
                    <PlayArrowIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={standardSelectedTextList ? "Copy selected standard model ids" : "No standard model selected"}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Copy selected standard model ids"
                      disabled={!standardSelectedTextList}
                      onClick={() => copyToClipboard(standardSelectedTextList, "Standard model IDs")}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Completion cost at or below ${premiumCutoffUsdPerMillion}/M output tokens.
            </Typography>
            {selectedStandardModelChips.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
                {selectedStandardModelChips.map((chip) => (
                  <Chip key={chip.key} label={chip.label} size="small" title={chip.id} />
                ))}
              </Box>
            )}
            <List dense>
              {standardByVendor.map((vendorGroup) => (
                <Box key={`standard-${vendorGroup.vendor}`}>
                  {(() => {
                    const split = splitLatestAndOlderVendorRows(vendorGroup.rows);
                    return (
                      <>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <ProviderAvatar provider={vendorGroup.vendor} />
                    </ListItemIcon>
                    <ListItemText
                      primary={vendorGroup.vendor}
                      secondary={`${vendorGroup.rows.length} models`}
                    />
                  </ListItem>
                  {split.latest.map((row) => (
                    <ListItem key={`standard-latest-${row.providerVariantKey}`} sx={{ pl: 4 }}>
                      <ListItemText
                        primary={getModelDisplayName(row.name)}
                        secondary={renderModelSubtitle("Latest", row)}
                        primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.25 }}
                      />
                      {renderModelActions(
                        row,
                        selectedStandardKeySet.has(row.providerVariantKey),
                        () => toggleStandardModel(row.providerVariantKey)
                      )}
                    </ListItem>
                  ))}
                  {!hideOlderModels && split.older.length > 0 && (
                    <>
                      <ListItem sx={{ pl: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                          Older models start
                        </Typography>
                      </ListItem>
                      <Divider sx={{ ml: 4, mr: 2 }} />
                    </>
                  )}
                  {!hideOlderModels &&
                    split.older.map((row) => (
                    <ListItem key={`standard-older-${row.providerVariantKey}`} sx={{ pl: 4 }}>
                      <ListItemText
                        primary={getModelDisplayName(row.name)}
                        secondary={renderModelSubtitle("Older", row)}
                        primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.25 }}
                      />
                      {renderModelActions(
                        row,
                        selectedStandardKeySet.has(row.providerVariantKey),
                        () => toggleStandardModel(row.providerVariantKey)
                      )}
                    </ListItem>
                  ))}
                      </>
                    );
                  })()}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6">Premium</Typography>
                <Chip label={`Selected ${selectedPremiumIds.length}`} size="small" color="primary" />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title="Test openrouter:premium">
                  <IconButton
                    size="small"
                    aria-label="Test openrouter premium group"
                    onClick={() => openChatTestForServiceGroup("openrouter:premium")}
                  >
                    <PlayArrowIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={premiumSelectedTextList ? "Copy selected premium model ids" : "No premium model selected"}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Copy selected premium model ids"
                      disabled={!premiumSelectedTextList}
                      onClick={() => copyToClipboard(premiumSelectedTextList, "Premium model IDs")}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Completion cost above ${premiumCutoffUsdPerMillion}/M output tokens.
            </Typography>
            {selectedPremiumModelChips.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
                {selectedPremiumModelChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    size="small"
                    color="primary"
                    variant="outlined"
                    title={chip.id}
                  />
                ))}
              </Box>
            )}
            <List dense>
              {premiumByVendor.map((vendorGroup) => (
                <Box key={`premium-${vendorGroup.vendor}`}>
                  {(() => {
                    const split = splitLatestAndOlderVendorRows(vendorGroup.rows);
                    return (
                      <>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <ProviderAvatar provider={vendorGroup.vendor} />
                    </ListItemIcon>
                    <ListItemText
                      primary={vendorGroup.vendor}
                      secondary={`${vendorGroup.rows.length} models`}
                    />
                  </ListItem>
                  {split.latest.map((row) => (
                    <ListItem key={`premium-latest-${row.providerVariantKey}`} sx={{ pl: 4 }}>
                      <ListItemText
                        primary={getModelDisplayName(row.name)}
                        secondary={renderModelSubtitle("Latest", row)}
                        primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.25 }}
                      />
                      {renderModelActions(
                        row,
                        selectedPremiumKeySet.has(row.providerVariantKey),
                        () => togglePremiumModel(row.providerVariantKey)
                      )}
                    </ListItem>
                  ))}
                  {!hideOlderModels && split.older.length > 0 && (
                    <>
                      <ListItem sx={{ pl: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                          Older models start
                        </Typography>
                      </ListItem>
                      <Divider sx={{ ml: 4, mr: 2 }} />
                    </>
                  )}
                  {!hideOlderModels &&
                    split.older.map((row) => (
                    <ListItem key={`premium-older-${row.providerVariantKey}`} sx={{ pl: 4 }}>
                      <ListItemText
                        primary={getModelDisplayName(row.name)}
                        secondary={renderModelSubtitle("Older", row)}
                        primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.25 }}
                      />
                      {renderModelActions(
                        row,
                        selectedPremiumKeySet.has(row.providerVariantKey),
                        () => togglePremiumModel(row.providerVariantKey)
                      )}
                    </ListItem>
                  ))}
                      </>
                    );
                  })()}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
}
